import moment from 'moment'

/**
 * Formats a date value for export using the same logic as dateFormat in formatter.js.
 * Returns a formatted string or empty string for invalid/missing dates.
 */
const formatDateValue = (value) => {
  if (!value) return ''
  if (typeof value !== 'string') return value
  const m = moment(value)
  if (!m.isValid()) return ''
  if (new Date(m).getFullYear() < 1800) return ''
  return moment.utc(value).format('MM/DD/YYYY')
}

/** Returns empty string for null/undefined and stray "null"/"undefined" strings from APIs */
const sanitize = (v) => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string' && (v === 'null' || v === 'undefined')) return ''
  return v
}

/**
 * Converts a DB column key like "FIRST_NAME" or "EMP_STATUS_DESC" to a
 * human-readable label like "First Name" or "Emp Status Desc".
 */
const humanizeKey = (key) => {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Heuristic: returns true if a string value looks like a date from the API.
 * Matches patterns like "2020-01-15 00:00:00.000" or ISO "2020-01-15T00:00:00.000Z".
 */
const looksLikeDate = (val) => {
  if (typeof val !== 'string') return false
  return /^\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}:\d{2}/.test(val)
}

/**
 * Processes a single value: sanitizes, auto-detects dates, and formats them.
 */
const processValue = (val) => {
  const clean = sanitize(val)
  if (looksLikeDate(clean)) return formatDateValue(clean)
  return clean
}

/**
 * Builds a structured data object from recordData and sections config.
 * Used by all three export functions.
 *
 * When a section has explicit `fields`, those are used (with labels and types).
 * When `fields` is omitted, ALL keys from the data are exported dynamically
 * with humanized labels and auto-detected date formatting.
 */
const buildExportData = (recordData, sections) => {
  const result = {}
  for (const section of sections) {
    const raw = recordData[section.key]
    if (!raw || (Array.isArray(raw) && raw.length === 0)) {
      if (section.type === 'flat' && raw && typeof raw === 'object') {
        // flat section with an empty-ish object, still include it
      } else if (section.type === 'array') {
        continue // skip empty array sections
      }
    }

    if (section.type === 'flat') {
      const obj = raw || {}
      if (section.fields) {
        const formatted = {}
        for (const field of section.fields) {
          const val = sanitize(obj[field.key])
          formatted[field.label] = field.type === 'date' ? formatDateValue(val) : val
        }
        result[section.label] = formatted
      } else {
        // Dynamic: export all keys with humanized labels
        const formatted = {}
        for (const [k, v] of Object.entries(obj)) {
          formatted[humanizeKey(k)] = processValue(v)
        }
        result[section.label] = formatted
      }
    } else if (section.type === 'array') {
      const rows = raw || []
      if (rows.length === 0) continue

      if (section.fields) {
        result[section.label] = rows.map((row) => {
          const formatted = {}
          for (const field of section.fields) {
            const val = sanitize(row[field.key])
            formatted[field.label] = field.type === 'date' ? formatDateValue(val) : val
          }
          return formatted
        })
      } else {
        // Dynamic: use all keys from first row, humanize labels
        const keys = Object.keys(rows[0])
        result[section.label] = rows.map((row) => {
          const formatted = {}
          for (const k of keys) {
            formatted[humanizeKey(k)] = processValue(row[k])
          }
          return formatted
        })
      }
    }
  }
  return result
}

/**
 * Triggers a file download in the browser using Blob + createObjectURL.
 */
const triggerDownload = (content, fileName, mimeType) => {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Appends the current date to a base file name.
 */
const buildFileName = (baseName, ext) => {
  const date = moment().format('YYYY-MM-DD')
  return `${baseName}_${date}.${ext}`
}

// --- CSV ---

/** Escapes a CSV cell value: wraps in quotes if it contains commas, quotes, or newlines */
const escapeCSV = (val) => {
  const str = String(val === undefined || val === null ? '' : val)
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Exports record data as CSV.
 * Flat sections become a single header+value row.
 * Array sections get a section header, column headers, and data rows.
 * Sections are separated by blank lines.
 */
export const exportToCSV = (recordData, sections, fileName) => {
  const data = buildExportData(recordData, sections)
  const lines = []

  for (const [sectionLabel, sectionData] of Object.entries(data)) {
    if (lines.length > 0) lines.push('') // blank separator

    lines.push(escapeCSV(sectionLabel))

    if (Array.isArray(sectionData)) {
      if (sectionData.length === 0) continue
      const headers = Object.keys(sectionData[0])
      lines.push(headers.map(escapeCSV).join(','))
      for (const row of sectionData) {
        lines.push(headers.map((h) => escapeCSV(row[h])).join(','))
      }
    } else {
      // Flat object: keys as header row, values as data row
      const keys = Object.keys(sectionData)
      lines.push(keys.map(escapeCSV).join(','))
      lines.push(keys.map((k) => escapeCSV(sectionData[k])).join(','))
    }
  }

  const content = lines.join('\r\n')
  triggerDownload(content, buildFileName(fileName, 'csv'), 'text/csv;charset=utf-8;')
}

// --- JSON ---

/**
 * Exports record data as formatted JSON.
 */
export const exportToJSON = (recordData, sections, fileName) => {
  const data = buildExportData(recordData, sections)
  const content = JSON.stringify(data, null, 2)
  triggerDownload(content, buildFileName(fileName, 'json'), 'application/json;charset=utf-8;')
}

// --- XML ---

/** Escapes special XML characters */
const escapeXML = (val) => {
  const str = String(val === undefined || val === null ? '' : val)
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/** Converts a string to a valid XML tag name: replace non-alphanumeric with underscore, ensure starts with letter */
const toXMLTag = (str) => {
  let tag = str.replace(/[^a-zA-Z0-9_]/g, '_')
  if (!/^[a-zA-Z]/.test(tag)) tag = '_' + tag
  return tag
}

/**
 * Exports record data as well-formed XML.
 */
export const exportToXML = (recordData, sections, fileName) => {
  const data = buildExportData(recordData, sections)
  const lines = ['<?xml version="1.0" encoding="UTF-8"?>', '<Record>']

  for (const [sectionLabel, sectionData] of Object.entries(data)) {
    const sectionTag = toXMLTag(sectionLabel)

    if (Array.isArray(sectionData)) {
      lines.push(`  <${sectionTag}>`)
      for (const row of sectionData) {
        lines.push('    <Row>')
        for (const [key, val] of Object.entries(row)) {
          const tag = toXMLTag(key)
          lines.push(`      <${tag}>${escapeXML(val)}</${tag}>`)
        }
        lines.push('    </Row>')
      }
      lines.push(`  </${sectionTag}>`)
    } else {
      lines.push(`  <${sectionTag}>`)
      for (const [key, val] of Object.entries(sectionData)) {
        const tag = toXMLTag(key)
        lines.push(`    <${tag}>${escapeXML(val)}</${tag}>`)
      }
      lines.push(`  </${sectionTag}>`)
    }
  }

  lines.push('</Record>')
  const content = lines.join('\n')
  triggerDownload(content, buildFileName(fileName, 'xml'), 'application/xml;charset=utf-8;')
}
